const AUTH_KEYS = {
  token: "token",
  role: "role",
  enterpriseId: "enterprise_id",
  enterpriseName: "enterprise_name",
  fullName: "full_name",
  pageAccess: "page_access",
  mustChangePassword: "must_change_password",
  passwordChanged: "password_changed",
  isDemo: "is_demo",
  demoExpiresAt: "demo_expires_at",
  sessionExpiresAt: "session_expires_at",
  lastActivityAt: "last_activity_at",
};

function base64UrlDecode(input) {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = normalized.length % 4;
  const padded = pad ? normalized + "=".repeat(4 - pad) : normalized;
  return atob(padded);
}

function decodeTokenPayload(token) {
  try {
    const payloadSegment = String(token || "").split(".")[1];
    if (!payloadSegment) return null;
    return JSON.parse(base64UrlDecode(payloadSegment));
  } catch {
    return null;
  }
}

function getFromSessionThenLocal(key) {
  const sessionVal = sessionStorage.getItem(key);
  if (sessionVal != null) return sessionVal;
  return localStorage.getItem(key);
}

function setInSessionOnly(key, value) {
  if (value == null || value === "") {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, String(value));
  // Ensure auth state never persists beyond browser lifetime.
  localStorage.removeItem(key);
}

export function clearAuthSessionStorage() {
  const values = Object.values(AUTH_KEYS);
  values.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
}

export function getAuthToken() {
  return getFromSessionThenLocal(AUTH_KEYS.token);
}

export function getAuthRole() {
  return getFromSessionThenLocal(AUTH_KEYS.role);
}

export function getEnterpriseId() {
  return getFromSessionThenLocal(AUTH_KEYS.enterpriseId);
}

function parseJsonArray(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getAuthSessionFromStorage() {
  const token = getAuthToken();
  if (!token) return null;

  const role = getFromSessionThenLocal(AUTH_KEYS.role);
  const enterprise_id = getFromSessionThenLocal(AUTH_KEYS.enterpriseId);
  const enterprise_name = getFromSessionThenLocal(AUTH_KEYS.enterpriseName) || "";
  const full_name = getFromSessionThenLocal(AUTH_KEYS.fullName) || "";
  const page_access = parseJsonArray(getFromSessionThenLocal(AUTH_KEYS.pageAccess));
  const must_change_password = getFromSessionThenLocal(AUTH_KEYS.mustChangePassword) === "true";
  const password_changed = getFromSessionThenLocal(AUTH_KEYS.passwordChanged) === "true";
  const is_demo = getFromSessionThenLocal(AUTH_KEYS.isDemo) === "true";
  const demo_expires_at = getFromSessionThenLocal(AUTH_KEYS.demoExpiresAt);
  let last_activity_at = getFromSessionThenLocal(AUTH_KEYS.lastActivityAt);
  let session_expires_at = getFromSessionThenLocal(AUTH_KEYS.sessionExpiresAt);

  if (!session_expires_at) {
    const payload = decodeTokenPayload(token);
    if (payload?.exp) {
      session_expires_at = new Date(payload.exp * 1000).toISOString();
      setInSessionOnly(AUTH_KEYS.sessionExpiresAt, session_expires_at);
    }
  }

  // Migrate old persistent auth keys into browser-session scope.
  setInSessionOnly(AUTH_KEYS.token, token);
  if (role) setInSessionOnly(AUTH_KEYS.role, role);
  if (enterprise_id != null) setInSessionOnly(AUTH_KEYS.enterpriseId, enterprise_id);
  if (enterprise_name) setInSessionOnly(AUTH_KEYS.enterpriseName, enterprise_name);
  if (full_name) setInSessionOnly(AUTH_KEYS.fullName, full_name);
  if (page_access.length) setInSessionOnly(AUTH_KEYS.pageAccess, JSON.stringify(page_access));
  if (must_change_password) setInSessionOnly(AUTH_KEYS.mustChangePassword, "true");
  if (password_changed) setInSessionOnly(AUTH_KEYS.passwordChanged, "true");
  if (is_demo) setInSessionOnly(AUTH_KEYS.isDemo, "true");
  if (demo_expires_at) setInSessionOnly(AUTH_KEYS.demoExpiresAt, demo_expires_at);
  if (session_expires_at) setInSessionOnly(AUTH_KEYS.sessionExpiresAt, session_expires_at);
  if (!is_demo) {
    if (!last_activity_at) {
      last_activity_at = new Date().toISOString();
    }
    setInSessionOnly(AUTH_KEYS.lastActivityAt, last_activity_at);
  }

  return { role, enterprise_id, enterprise_name, full_name, page_access, must_change_password, password_changed, is_demo, demo_expires_at, session_expires_at, last_activity_at };
}

export function setAuthSession(authPayload = {}) {
  const token = authPayload?.accessToken || authPayload?.token || getAuthToken();
  const role = authPayload?.role;
  const enterprise_id = authPayload?.enterprise_id;
  const enterprise_name = authPayload?.enterprise_name || "";
  const full_name = authPayload?.full_name || "";
  const page_access = Array.isArray(authPayload?.page_access) ? authPayload.page_access : [];
  const must_change_password = authPayload?.must_change_password === true;
  const password_changed = authPayload?.password_changed === true;
  const is_demo = authPayload?.is_demo === true;

  if (!token) throw new Error("Login response missing token");

  setInSessionOnly(AUTH_KEYS.token, token);
  if (role) setInSessionOnly(AUTH_KEYS.role, role);
  if (enterprise_id != null) setInSessionOnly(AUTH_KEYS.enterpriseId, enterprise_id);
  if (enterprise_name) setInSessionOnly(AUTH_KEYS.enterpriseName, enterprise_name);
  else {
    sessionStorage.removeItem(AUTH_KEYS.enterpriseName);
    localStorage.removeItem(AUTH_KEYS.enterpriseName);
  }
  if (full_name) setInSessionOnly(AUTH_KEYS.fullName, full_name);
  else {
    sessionStorage.removeItem(AUTH_KEYS.fullName);
    localStorage.removeItem(AUTH_KEYS.fullName);
  }
  if (page_access.length) setInSessionOnly(AUTH_KEYS.pageAccess, JSON.stringify(page_access));
  else {
    sessionStorage.removeItem(AUTH_KEYS.pageAccess);
    localStorage.removeItem(AUTH_KEYS.pageAccess);
  }
  if (must_change_password) setInSessionOnly(AUTH_KEYS.mustChangePassword, "true");
  else {
    sessionStorage.removeItem(AUTH_KEYS.mustChangePassword);
    localStorage.removeItem(AUTH_KEYS.mustChangePassword);
  }
  if (password_changed) setInSessionOnly(AUTH_KEYS.passwordChanged, "true");
  else {
    sessionStorage.removeItem(AUTH_KEYS.passwordChanged);
    localStorage.removeItem(AUTH_KEYS.passwordChanged);
  }

  let session_expires_at = authPayload?.expires_at || null;
  if (!session_expires_at) {
    const payload = decodeTokenPayload(token);
    if (payload?.exp) {
      session_expires_at = new Date(payload.exp * 1000).toISOString();
    }
  }
  if (session_expires_at) {
    setInSessionOnly(AUTH_KEYS.sessionExpiresAt, session_expires_at);
  } else {
    sessionStorage.removeItem(AUTH_KEYS.sessionExpiresAt);
    localStorage.removeItem(AUTH_KEYS.sessionExpiresAt);
  }

  let demo_expires_at = null;
  let last_activity_at = null;
  if (is_demo) {
    setInSessionOnly(AUTH_KEYS.isDemo, "true");
    demo_expires_at = authPayload?.expires_at || new Date(Date.now() + 30 * 60 * 1000).toISOString();
    setInSessionOnly(AUTH_KEYS.demoExpiresAt, demo_expires_at);
    sessionStorage.removeItem(AUTH_KEYS.lastActivityAt);
    localStorage.removeItem(AUTH_KEYS.lastActivityAt);
  } else {
    sessionStorage.removeItem(AUTH_KEYS.isDemo);
    sessionStorage.removeItem(AUTH_KEYS.demoExpiresAt);
    localStorage.removeItem(AUTH_KEYS.isDemo);
    localStorage.removeItem(AUTH_KEYS.demoExpiresAt);
    last_activity_at = new Date().toISOString();
    setInSessionOnly(AUTH_KEYS.lastActivityAt, last_activity_at);
  }

  return { role, enterprise_id, enterprise_name, full_name, page_access, must_change_password, password_changed, is_demo, demo_expires_at, session_expires_at, last_activity_at };
}

export function isAuthSessionExpired() {
  const expiry = getFromSessionThenLocal(AUTH_KEYS.sessionExpiresAt);
  if (!expiry) return false;
  const expiryMs = Date.parse(expiry);
  if (Number.isNaN(expiryMs)) return false;
  return Date.now() >= expiryMs;
}

export function getLastActivityAt() {
  return getFromSessionThenLocal(AUTH_KEYS.lastActivityAt);
}

export function markUserActivity(iso = new Date().toISOString()) {
  setInSessionOnly(AUTH_KEYS.lastActivityAt, iso);
  return iso;
}
